import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardTitle } from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import Badge from '../../../components/bootstrap/Badge';
import Spinner from '../../../components/bootstrap/Spinner';
import DevisLigneRow from './DevisLigneRow';
import DemandePrixSelectionModal from './DemandePrixSelectionModal';
import { formatCurrency } from '../../comptabilite/utils/functions';
import { isMobile, isTablet } from 'react-device-detect';
import { Col, Row } from 'react-bootstrap';
import SimulatorPopUp from './Simulateur/SimulatorPopUp';
import { useLogicDevis } from '../hooks/useLogicDevis';
import { useLogicProduit } from 'pages/Stock/hooks/useLogicProduit';
import SimulatorIcon from 'assets/customizeIcons/simulator.svg';
import SimulatorPopUpFacile from './SimulateurFacile/SimulatorPopUpFacile';
import ForfaitsSM from 'constants/simulatorDataFacile/Forfaits';
import MainOuvreData from 'constants/simulatorDataFacile/MainOuvre';
import simulatorData from 'constants/simulatorDataDetails';

const DevisLignesManager = ({ lignes = [], onLignesChange, loading, products = [], company }) => {
	const [showDemandePrixModal, setShowDemandePrixModal] = useState(false);
	const [produitsDemandesPrix, setProduitsDemandesPrix] = useState([]);
	const [editModalSimulator, setEditModalSimulator] = useState(false);
	const [editModalForfait, setEditModalForfait] = useState(false);
	const [currentFormValue, setCurrentFormValue] = useState({});
	const [addSimulatorLoading, setAddSimulatorLoading] = useState(false);
	const { createProduit } = useLogicProduit();
	// Use react-device-detect for device detection
	const shouldRenderMobileCard = isMobile || isTablet;
	const isDesktop = !isMobile && !isTablet;
	useEffect(() => {
		console.log('is desktop, is mobile , is tablette ', isDesktop, isMobile, isTablet);
	}, []);

	const addLigne = useCallback(
		(type = 'produit') => {
			const newLigne = {
				type,
				produitId: null,
				nom: '', // CORRECTION: Initialiser le champ nom
				description: '',
				quantite: 1,
				unite: 'Piece',
				prixUnitaireHT: 0,
				categorie: 'materiel',
			};

			if (type === 'service') {
				newLigne.description = 'Service personnalisé';
				newLigne.nom = 'Service'; // CORRECTION: Définir le nom pour les services
				newLigne.unite = 'Hour';
			} else if (type === 'note') {
				newLigne.description = 'Note';
				newLigne.nom = 'Note'; // CORRECTION: Définir le nom pour les notes
				newLigne.quantite = 1;
				newLigne.prixUnitaireHT = 0;
			} else if (type === 'produit') {
				newLigne.nom = 'Produit'; // CORRECTION: Définir le nom par défaut pour les produits
			}

			onLignesChange([...(lignes || []), newLigne]);
		},
		[lignes, onLignesChange],
	);

	const addDeplacement = () => {
		const newLigne = {
			type: 'service',
			produitId: null,
			nom: 'Déplacement', // CORRECTION: Initialiser le champ nom
			description: 'Déplacement',
			quantite: 1,
			unite: 'Piece',
			prixUnitaireHT: 50,
		};

		onLignesChange([...(lignes || []), newLigne]);
	};
	const addGarantie = () => {
		const newLigne = {
			type: 'note',
			produitId: null,
			nom: 'Garantie', // CORRECTION: Initialiser le champ nom
			description: 'Garantie 5 ans',
			quantite: 1,
			prixUnitaireHT: 0,
		};

		onLignesChange([...(lignes || []), newLigne]);
	};

	// Ajouter un produit depuis une demande de prix
	const handleAddFromDemandePrix = useCallback(
		(selection) => {
			const newLigne = {
				type: 'produit',
				produitId: selection.produitInfo.produitId,
				nom: selection.produitInfo.nom || selection.produitInfo.description, // CORRECTION: Utiliser le nom ou la description
				description: selection.produitInfo.description,
				quantite: selection.produitInfo.quantite,
				unite: selection.produitInfo.unite,
				prixUnitaireHT: selection.produitInfo.prix,
				categorie: 'materiel',
				// Métadonnées pour le suivi
				sourceDemandePrix: selection.demandePrixId,
				sourceReponse: selection.reponseId,
				fournisseur: selection.produitInfo.fournisseur,
				delaiLivraison: selection.produitInfo.delaiLivraison,
			};

			onLignesChange([...(lignes || []), newLigne]);

			setProduitsDemandesPrix((prev) => [
				...prev,
				{
					demandePrixId: selection.demandePrixId,
					reponseId: selection.reponseId,
				},
			]);
		},
		[lignes, onLignesChange],
	);

	const updateLigne = useCallback(
		(index, field, value) => {
			const newLignes = [...(lignes || [])];
			newLignes[index][field] = value;
			onLignesChange(newLignes);
		},
		[lignes, onLignesChange],
	);

	const removeLigne = useCallback(
		(index) => {
			const ligneToRemove = lignes[index];

			if (ligneToRemove.sourceDemandePrix && ligneToRemove.sourceReponse) {
				setProduitsDemandesPrix((prev) =>
					prev.filter(
						(prod) =>
							!(
								prod.demandePrixId === ligneToRemove.sourceDemandePrix &&
								prod.reponseId === ligneToRemove.sourceReponse
							),
					),
				);
			}

			const newLignes = (lignes || []).filter((_, i) => i !== index);
			onLignesChange(newLignes);
		},
		[lignes, onLignesChange],
	);

	const calculateGlobalStockStatus = useCallback((lignes) => {
		const produitsAvecStockInsuffisant = (lignes || []).filter(
			(ligne) =>
				ligne.type === 'produit' && ligne.stockStatus && !ligne.stockStatus.suffisant,
		);

		return {
			hasInsufficientStock: produitsAvecStockInsuffisant.length > 0,
			insufficientCount: produitsAvecStockInsuffisant.length,
			totalProducts: (lignes || []).filter((l) => l.type === 'produit').length,
		};
	}, []);

	const globalStockStatus = calculateGlobalStockStatus(lignes);

	const calculateLigneTotal = (ligne) => {
		const quantite = parseFloat(ligne?.quantite) || 0;
		const prixUnitaireHT = parseFloat(ligne?.prixUnitaireHT) || 0;
		return prixUnitaireHT * quantite;
	};

	const totalHT = (lignes || []).reduce((acc, ligne) => {
		return acc + calculateLigneTotal(ligne);
	}, 0);

	const hasDemandePrixProducts = (lignes || []).some((l) => l?.sourceDemandePrix);

	// Responsive action buttons
	const renderActionButtons = () => (
		<div className='d-flex gap-2 flex-wrap flex-column'>
			<div className='d-flex gap-3 row'>
				<Button className='col-auto'
					color='primary'
					// icon='Add'
					onClick={() => setEditModalSimulator(true)}
					disabled={loading}>
					Fourniture
				</Button>
				<Button className='col-auto'
					color='primary'
					// icon='Add'
					onClick={() => setEditModalForfait('forfaits')}
					disabled={loading}>
					Forfaits
				</Button>
				<Button className='col-auto'
					color='primary'
					// icon='Add'
					onClick={() => setEditModalForfait('main')}
					disabled={loading}>
					Main d'oeuvre
				</Button>
				<Button className='col-auto' color='primary' onClick={() => addDeplacement()} disabled={loading}>
					Déplacement
				</Button>
				<Button
					color='primary'
					onClick={() => addGarantie()}
					disabled={loading}
					className='border border-primary col-auto'>
					Garantie
				</Button>
			</div>
			<div className='row d-flex gap-3'>
				<Button
					color='primary'
					isOutline
					icon='Note'
					onClick={() => addLigne('note')}
					disabled={loading}
					className='border border-primary col-auto'>
					{shouldRenderMobileCard ? 'Note' : 'Ajouter Note'}
				</Button>
				{/* <Button
					color='primary'
					isOutline
					icon='Add'
					onClick={() => addLigne('produit')}
					disabled={loading}
					className='border border-primary col-auto'>
					{shouldRenderMobileCard ? 'Produit' : 'Ajouter Produit'}
				</Button> */}

				<Button
					color='primary'
					isOutline
					icon='Add'
					onClick={() => addLigne('service')}
					disabled={loading}
					className='border border-primary col-auto'>
					{shouldRenderMobileCard ? 'Ligne' : 'Ajouter Ligne'}
				</Button>
			</div>

			{/* 			<Button
				color='primary'
				isOutline
				icon='PriceCheck'
				onClick={() => setShowDemandePrixModal(true)}
				disabled={loading}
				
				className='border border-primary'>
				{shouldRenderMobileCard ? 'Dem. Prix' : 'Depuis Demandes Prix'}
			</Button> 
			*/}
		</div>
	);
	const handelCreateForfaitLine = async () => {
		setAddSimulatorLoading(true);

		try {
			const description = currentFormValue?.productFinal;
			// const description = formatDescription(currentFormValue);
			const prixUnitaireHT = currentFormValue['price'];
			const lineToAdd = {
				type: 'service',
				nom: `Forfaits`,
				description,
				quantite: 1,
				unite: 'Piece',
				prixUnitaireHT,
			};

			setAddSimulatorLoading(false);
			setCurrentFormValue({});
			setEditModalForfait(false);
			onLignesChange([...(lignes || []), lineToAdd]);
		} catch (err) {
			console.error('❌ Error:', err);
		} finally {
			setAddSimulatorLoading(false);
		}
	};

	const handelCreateMainDoeuvreLine = async () => {
		setAddSimulatorLoading(true);

		try {
			const description = `Main d'oeuvre:\n${currentFormValue?.productFinal}`;
			// const description = formatDescription(currentFormValue);
			const prixUnitaireHT = currentFormValue['price'];
			const lineToAdd = {
				type: 'service',
				nom: `Main d'oeuvre`,
				description,
				quantite: 1,
				unite: 'Piece',
				prixUnitaireHT,
			};

			setAddSimulatorLoading(false);
			setCurrentFormValue({});
			setEditModalForfait(false);
			onLignesChange([...(lignes || []), lineToAdd]);
		} catch (err) {
			console.error('❌ Error:', err);
		} finally {
			setAddSimulatorLoading(false);
		}
	};
	const handelAddSimulatorLine = async () => {
		setAddSimulatorLoading(true);

		const getValue = (key) =>
			currentFormValue?.ordered?.find((i) => i?.key === key)?.originalValue;

		/* -------------------- shared values -------------------- */
		const typeProduct = getValue("typeproduct");
		const typeInstallation = getValue("typeInstallation");
		const Moteur = getValue("Moteur");
		const typeMotor = getValue("typeMotor");
		const puissance = getValue("puissance");

		const largeur = Number(getValue("largeur")) || 0;
		const hauteur = Number(getValue("hauteur")) || 0;
		const lameTaille = String(getValue("lameTaille") || "");
		const coulisse = String(getValue("coulisse") || "").toLowerCase();

		const surfaceM2 = (largeur * hauteur) / 1000000;
		const isSmallLame = ["37", "43"].includes(lameTaille);

		/* -------------------- helpers -------------------- */
		const round = (v) => Math.round(v * 100) / 100;

		const epaisseurLame = {
			37: "7 mm",
			43: "9 mm",
			55: "13 mm",
		}[lameTaille] || "";

		/* -------------------- pricing -------------------- */
		const getPrixUnitaireHT = () => {
			/* KIT MANUEL */
			if (typeProduct === "Kit Manuel") {
				return typeInstallation === "Traditionnel"
					? largeur < 2000 ? 320 : 440
					: largeur < 2000 ? 280 : 380;
			}


			if (typeProduct === "Kit Motorisation" || typeProduct === "Moteur") {
				const puissanceValue = Number(puissance); // "10", "20", ...

				const steps = Math.max((puissanceValue / 10) - 1, 0);

				if (typeMotor === "radio") {
					const base = 228.98;
					const increment = 70;

					return round(base + steps * increment);
				}

				if (typeMotor === "filaire") {
					const base = 188.98;
					const increment = 70;

					return round(base + steps * increment);
				}
			}
			/* TABLIER */
			if (typeProduct === "Tablier Volet roulant") {
				let prix =
					surfaceM2 *
					(isSmallLame ? 55 : 75) +
					(isSmallLame ? 115 : 120);

				if (coulisse === "oui") prix += 120;

				return round(prix);
			}

			/* VOLET MANUEL COMPLET */
			if (typeProduct === "Volet Roulant Complet Manuel") {
				let prix =
					surfaceM2 *
					(isSmallLame ? 55 : 75) +
					(isSmallLame ? 115 : 120) +
					198.98;

				return round(prix);
			}

			/* VOLET MOTORISÉ COMPLET */
			if (typeProduct === "Volet Roulant Complet Motorisé") {
				const baseRate = isSmallLame ? 55 : 75;
				const baseFixed = isSmallLame ? 115 : 120;

				let prix = surfaceM2 * baseRate + baseFixed;

				if (Moteur !== "Somfy") {
					const extra = {
						filaire: 209.98,
						radio: 279.98,
						solaire: 279.98,
					};
					prix += extra[typeMotor] || 0;
				} else {
					const somfy = {
						filaire: isSmallLame ? 500 : 600,
						radio: isSmallLame ? 550 : 650,
						solaire: isSmallLame ? 600 : 700,
					};

					const rate = isSmallLame ? 70 : 85;
					prix = surfaceM2 * rate + (somfy[typeMotor] || 0);
				}

				return round(prix);
			}

			return currentFormValue?.price || 0;
		};

		/* -------------------- description -------------------- */
		const getDescription = () => {
			const ressort = getValue("ressort");
			const typeMesureLargeur = getValue("typeMesureLargeur");
			const coteManivelle = getValue("cote_manivelle");
			const matiereOldLame = getValue("matiereOldLame");
			const typeMesureHauteur = getValue("typeMesureHauteur");

			const couleur = getValue("Couleur de lame");
			const coulisseState = getValue("coulisse");
			const couleurCoulisse = getValue("Couleur de coulisse");

			const agraffe = getValue("isWithAgraffe");
			const nbrAgraffe = getValue("attaches");

			const axe = getValue("axe");
			const manoeuvreManuel = getValue("manoeuvreManuel");

			const poussoir = getValue("poussoir");
			const telecommande = getValue("telecommande");
			const typeTelecommande = getValue("typeTelecommande");
			const puissance = getValue("puissance");

			/* KIT MANUEL */
			if (typeProduct === "Kit Manuel") {
				if (typeInstallation === "Traditionnel") {
					return `
						Remplacement Mécanisme Complet Volet Roulant ${typeInstallation} à Manivelle :
- Dépose de l'ensemble du mécanisme défectueux.
- Fourniture et remplacement de l'axe d'enroulement complet ${matiereOldLame == "Bois" ? "avec ressort" : ressort}.
- Fourniture et remplacement du couple conique.
- Fourniture et remplacement de la sortie de caisson hexagonale.
- Fourniture et remplacement de la manivelle complète.
- Fourniture et remplacement des attaches de tablier.
- Réglage du tablier et contrôle de l'enroulement.
- Vérification de la manœuvre et des points d'arrêt.
- Essais complets et remise en service du volet roulant.

- Largeur : ${largeur} mm ${typeMesureLargeur}
- Côté manœuvre : ${coteManivelle}
- Matière tablier existant : ${matiereOldLame}
				`.trim();
				}

				return `
					Remplacement Mécanisme Complet Volet Roulant ${typeInstallation} à Manivelle:
- Dépose de l'ensemble du mécanisme défectueux.
- Fourniture et remplacement de l'axe d'enroulement complet.
- Fourniture et remplacement du TREUIL TECHNIVIS.
- Fourniture et remplacement de la sortie de caisson hexagonale.
- Fourniture et remplacement de la manivelle complète.
- Fourniture et remplacement des attaches de tablier.
- Réglage du tablier et contrôle de l'enroulement.
- Vérification de la manœuvre et des points d'arrêt.
- Essais complets et remise en service du volet roulant.

- Largeur : ${largeur} mm ${typeMesureLargeur}
- Côté manœuvre : ${coteManivelle}
			`.trim();
			}

			/* TABLIER */
			if (typeProduct === "Tablier Volet roulant") {
				const detailsCoulisse =
					coulisseState === "oui"
						? `Avec coulisses coloris ${couleurCoulisse}`
						: "";

				const detailsAgraffe =
					agraffe === "oui" ? `, avec ${nbrAgraffe} agraffe` : "";

				return `
Fourniture d’un tablier volet roulant ${typeInstallation} en aluminium ${couleur} .
- MARQUE BUILDING, Lames aluminium DP${lameTaille}, épaisseur ${epaisseurLame} double paroi avec mousse polyuréthane injectée densité env. 75 kg/m³.
- Isolation thermique additionnelle ΔR jusqu’à 0,22 m².K/W.
- Dimensions : Largeur = ${largeur} mm ${typeMesureLargeur}, Hauteur = ${hauteur} mm ${typeMesureHauteur}.
- ${detailsCoulisse} ${detailsAgraffe}.
- Confort acoustique : réduction significative des bruits extérieurs (-5 dB).
- Structure aluminium thermolaquée résistante à la corrosion, aux UV et aux intempéries.
			`.trim();
			}

			/* COMPLET MANUEL */
			if (typeProduct === "Volet Roulant Complet Manuel") {
				return `
Fourniture d’un volet roulant ${typeInstallation} manuel en aluminium ${couleur}.
- MARQUE BUILDING
- Tablier en lames aluminium DP${lameTaille}, épaisseur ${epaisseurLame} double paroi avec mousse polyuréthane injectée densité env. 75 kg/m³.
- Isolation thermique additionnelle ΔR jusqu’à 0,22 m².K/W.
- Dimensions : Largeur ${largeur} mm ${typeMesureLargeur} x Hauteur ${hauteur} mm ${typeMesureHauteur}.
- Confort acoustique : réduction significative des bruits extérieurs (-5 dB).
- Structure aluminium thermolaquée résistante à la corrosion, aux UV et aux intempéries.
- Axe d’enroulement ${axe}
- Manœuvre par ${manoeuvreManuel} côté ${coteManivelle}
			`.trim();
			}

			/* COMPLET MOTORISÉ */
			if (typeProduct === "Volet Roulant Complet Motorisé") {
				return `
Fourniture d’un volet roulant ${typeInstallation} motorisé en aluminium ${couleur}.
- Tablier en lames aluminium DP${lameTaille}, épaisseur ${epaisseurLame} double paroi avec mousse polyuréthane injectée densité env. 75 kg/m³.
- Isolation thermique additionnelle ΔR jusqu’à 0,22 m².K/W.
- Dimensions : Largeur ${largeur} mm ${typeMesureLargeur} x Hauteur ${hauteur} mm ${typeMesureHauteur}.
- Confort acoustique : réduction significative des bruits extérieurs (-5 dB).
- Structure aluminium thermolaquée résistante à la corrosion, aux UV et aux intempéries.
- Motorisation ${typeMotor} ${Moteur} ${puissance ? `puissance ${puissance}` : ""} ${typeMotor === "solaire" ? "avec panneau photovoltaïque." : ""
					} ${poussoir || ""}
${typeMotor === "solaire" ? "- Batterie autonome Li-ion : jusqu’à 30 jours d’autonomie sans recharge solaire." : ""}
${telecommande === "Avec télécommande"
						? `- Commande radio ${typeTelecommande || ""} IO-Homecontrol compatible domotique connectée.`
						: ""
					}
			`.trim();
			}

			return currentFormValue?.productFinal;
		};

		/* -------------------- final -------------------- */
		const prixUnitaireHT = getPrixUnitaireHT();

		const newLine = {
			type: "service",
			nom: "fourniture",
			description: getDescription(),
			prixUnitaireHT,
			quantite: 1,
			unite: "Piece",
			categorie: "materiel",
		};

		setAddSimulatorLoading(false);
		setCurrentFormValue({});
		setEditModalSimulator(false);

		onLignesChange([...(lignes || []), { ...newLine }]);
	};

	const handelCancel = () => {
		// Your implementation
	};
	return (
		<>
			<Card className='border border-gray-300 bg-white'>
				<CardHeader className='border-bottom border-gray-300 bg-white'>
					<div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center'>
						{/* <CardTitle className='text-dark mb-2 mb-md-0'>
							<Icon icon='List' className='me-2 text-dark' />
							Articles du Devis
							{globalStockStatus.hasInsufficientStock && (
								<Badge color='warning' className='ms-2'>
									<Icon icon='Warning' className='me-1' />
									Stock insuffisant ({globalStockStatus.insufficientCount}/
									{globalStockStatus.totalProducts} produits)
								</Badge>
							)}
						</CardTitle> */}
						{renderActionButtons()}
					</div>
				</CardHeader>

				<CardBody>
					{(lignes || []).length === 0 ? (
						<div className='text-center py-5 border border-gray-300 rounded bg-white'>
							<Icon icon='List' size='3x' className='text-dark mb-3' />
							<h5 className='text-dark mb-3'>Aucun article ajouté</h5>
							{/* <div className='d-flex gap-2 justify-content-center flex-wrap'>
								<Button
									color='primary'
									isOutline
									icon='Add'
									onClick={() => addLigne('produit')}
									size='sm'
									className='border border-primary'>
									Produit
								</Button>
								<Button
									color='primary'
									isOutline
									icon='Build'
									onClick={() => addLigne('service')}
									size='sm'
									className='border border-primary'>
									Service
								</Button>
								<Button
									color='primary'
									isOutline
									icon='PriceCheck'
									onClick={() => setShowDemandePrixModal(true)}
									size='sm'
									className='border border-primary'>
									{shouldRenderMobileCard ? 'Dem. Prix' : 'Demandes Prix'}
								</Button> 
							</div> */}
						</div>
					) : (
						<>
							{hasDemandePrixProducts && (
								<div className='border border-gray-300 bg-white p-3 d-flex align-items-center mb-3'>
									<Icon icon='PriceCheck' className='me-2 text-dark' />
									<span className='text-dark'>
										Ce devis contient des produits sélectionnés depuis des
										demandes de prix
									</span>
								</div>
							)}

							{/* Desktop table view - only show table header on desktop */}
							{isDesktop ? (
								<div className=''>
									<table className='table table-sm'>
										<thead className='bg-light'>
											<tr>
												{/* <th
													width='200'
													className='text-dark border-bottom border-gray-300'>
													Article
												</th> */}
												{/* <th
													width='200'
													className='text-dark border-bottom border-gray-300'>
													Nom
												</th> */}
												<th
													width='250'
													className='text-dark border-bottom border-gray-300'>
													Description
												</th>
												<th
													width='80'
													className='text-center text-dark border-bottom border-gray-300'>
													Qté
												</th>
												<th
													width='100'
													className='text-end text-dark border-bottom border-gray-300'>
													Prix HT
												</th>
												<th
													width='100'
													className='text-end text-dark border-bottom border-gray-300'>
													Total HT
												</th>
												<th
													width='60'
													className='text-center text-dark border-bottom border-gray-300'>
													Actions
												</th>
											</tr>
										</thead>
										<tbody>
											{(lignes || []).map((ligne, index) => (
												<DevisLigneRow
													key={index}
													ligne={ligne}
													index={index}
													onUpdate={updateLigne}
													onRemove={removeLigne}
													loading={loading}
													products={products}
													company={company}
												/>
											))}
										</tbody>
										<tfoot>
											<tr className='bg-light'>
												<td
													colSpan='4'
													className='text-end text-dark'>
													Total HT:
												</td>
												<td className='text-end fw-bold fs-5 '>
													{formatCurrency(totalHT, company?.currency)}
												</td>
												<td></td>
											</tr>
										</tfoot>
									</table>
								</div>
							) : (
								// Mobile/Tablet card view
								<div>
									{(lignes || []).map((ligne, index) => (
										<DevisLigneRow
											key={index}
											ligne={ligne}
											index={index}
											onUpdate={updateLigne}
											onRemove={removeLigne}
											loading={loading}
											products={products}
											company={company}
										/>
									))}

									{/* Total display for mobile/tablet */}
									<div className='border-top pt-3 mt-3'>
										<div className='d-flex justify-content-between align-items-center'>
											<span className='fw-bold text-dark'>Total HT:</span>
											<span className='fw-bold text-primary fs-5'>
												{formatCurrency(totalHT, company?.currency)}
											</span>
										</div>
									</div>
								</div>
							)}
						</>
					)}
				</CardBody>
			</Card>

			<DemandePrixSelectionModal
				isOpen={showDemandePrixModal}
				setIsOpen={setShowDemandePrixModal}
				onSelect={handleAddFromDemandePrix}
				selectedProducts={produitsDemandesPrix}
			/>

			{editModalSimulator && (
				<SimulatorPopUp
					setEditModalSimulator={setEditModalSimulator}
					data={{
						order_id: 1,
						modalTitle: 'Conception de produits et simulation',
						cardTitle: 'Simulate Product',
					}}
					simulatorData={simulatorData}
					currentFormValue={currentFormValue}
					setCurrentFormValue={setCurrentFormValue}
					handelCreateLine={handelAddSimulatorLine}
					handelCancel={handelCancel}>
					<ChildButtonComponent
						currentFormValue={currentFormValue}
						handelAddSimulatorLine={handelAddSimulatorLine}
						addSimulatorLoading={addSimulatorLoading}
					/>
				</SimulatorPopUp>
			)}

			{!!editModalForfait && (
				<SimulatorPopUpFacile
					setEditModalSimulator={setEditModalForfait}
					simulatorData={editModalForfait == 'forfaits' ? ForfaitsSM : MainOuvreData}
					currentFormValue={currentFormValue}
					setCurrentFormValue={setCurrentFormValue}>
					<Row className='px-2 px-md-3'>
						<Button
							color='primary'
							onClick={() => {
								editModalForfait == 'forfaits'
									? handelCreateForfaitLine()
									: handelCreateMainDoeuvreLine();
							}}
							disabled={
								!(
									currentFormValue &&
									typeof currentFormValue === 'object' &&
									Object.values(currentFormValue).some(
										(v) =>
											v !== null &&
											v !== undefined &&
											String(v).trim() !== '',
									)
								) || addSimulatorLoading
							}
							style={{
								borderRadius: '10px',
								width: '100%',
							}}>
							{addSimulatorLoading ? (
								<>
									<span className='spinner-border spinner-border-sm me-2' />
									Loading...
								</>
							) : (
								'Sauvegarder'
							)}
						</Button>
					</Row>
				</SimulatorPopUpFacile>
			)}
		</>
	);
};

const ChildButtonComponent = ({
	currentFormValue,
	handelAddSimulatorLine,
	addSimulatorLoading,
}) => {
	return (
		<Row className='px-3'>
			<Col>
				{/* {currentFormValue?.complet && ( */}

				<Button

					onClick={handelAddSimulatorLine}
					disabled={!currentFormValue?.complet || addSimulatorLoading}
					style={{
						borderRadius: '10px',
						width: '100%',
						backgroundColor: 'rgba(23, 162, 184, 0.1)',
						border: '1px solid #17a2b8',
					}}>
					{!!currentFormValue?.complet ? addSimulatorLoading ? (
						<>
							<span className='spinner-border spinner-border-sm me-2' />
							Loading...
						</>
					) : (
						<span style={{ color: '#17a2b8', fontWeight: '600' }}>
							Ajouter au devis
						</span>
					) : "⚠️ Simulation incomplète"}

				</Button>
				{/* )} */}
			</Col>
		</Row>
	);
};
export default DevisLignesManager;
